import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { Loader2, MessageSquare, ClipboardCheck, CalendarClock, Sparkles } from "lucide-react";
import { adminListActivity } from "@/lib/portal/admin.functions";

export const Route = createFileRoute("/_authenticated/admin/activity")({
  component: ActivityPage,
});

function fmt(ts: string) {
  try { return new Date(ts).toLocaleString(); } catch { return ts; }
}

function Section({ title, icon: Icon, count, children }: { title: string; icon: any; count: number; children: React.ReactNode }) {
  return (
    <section className="rounded-2xl border border-white/10 bg-white/[0.02]">
      <header className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
        <div className="flex items-center gap-2 text-white">
          <Icon className="size-4 text-amber-300" />
          <h2 className="font-serif text-lg">{title}</h2>
        </div>
        <span className="text-[11px] uppercase tracking-widest text-amber-300">{count}</span>
      </header>
      <div className="p-3">{children}</div>
    </section>
  );
}

function ActivityPage() {
  const fn = useServerFn(adminListActivity);
  const { data, isLoading } = useQuery({ queryKey: ["admin", "activity"], queryFn: () => fn(), refetchInterval: 60000 });

  if (isLoading || !data) {
    return <div className="py-20 grid place-items-center"><Loader2 className="size-6 animate-spin text-amber-300" /></div>;
  }

  const empty = data.counts.total === 0;

  return (
    <div className="space-y-6">
      <header>
        <p className="text-amber-300 text-[11px] tracking-[0.3em] uppercase font-semibold">Activity</p>
        <h1 className="font-serif text-4xl text-white mt-1">What needs your attention</h1>
        <p className="text-zinc-400 text-sm mt-2">
          {data.counts.unreadMessages} unread · {data.counts.tasksDue} tasks due · {data.counts.upcomingAppointments} upcoming appts · {data.counts.newLeads} new leads
        </p>
      </header>

      {empty ? (
        <div className="rounded-3xl border border-white/10 bg-white/[0.02] p-12 text-center">
          <p className="text-white font-serif text-xl">You're all caught up.</p>
          <p className="text-zinc-400 text-sm mt-2">New activity will appear here.</p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          <Section title="New messages" icon={MessageSquare} count={data.counts.unreadMessages}>
            {data.messages.length === 0 ? (
              <p className="text-zinc-500 text-sm px-2 py-4">No unread messages.</p>
            ) : (
              <ul className="divide-y divide-white/5">
                {data.messages.map((m: any) => (
                  <li key={m.projectId}>
                    <Link
                      to="/admin/customers/$projectId"
                      params={{ projectId: m.projectId }}
                      search={{ tab: "messages" } as any}
                      className="flex items-start justify-between gap-3 px-3 py-3 rounded-lg hover:bg-white/[0.04]"
                    >
                      <div className="min-w-0">
                        <p className="text-white text-sm font-medium truncate">{m.project?.customer_name || m.project?.project_name || "Customer"}</p>
                        <p className="text-zinc-400 text-xs truncate">{m.latest?.body || "New message"}</p>
                      </div>
                      <span className="shrink-0 rounded-full bg-amber-300/20 text-amber-200 text-[11px] px-2 py-0.5">{m.count}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <Section title="Tasks due" icon={ClipboardCheck} count={data.counts.tasksDue}>
            {data.tasks.length === 0 ? (
              <p className="text-zinc-500 text-sm px-2 py-4">Nothing due today.</p>
            ) : (
              <ul className="divide-y divide-white/5">
                {data.tasks.map((t: any) => (
                  <li key={t.id}>
                    <Link to="/admin/tasks" className="block px-3 py-3 rounded-lg hover:bg-white/[0.04]">
                      <p className="text-white text-sm font-medium truncate">{t.title}</p>
                      <p className="text-zinc-400 text-xs">
                        {t.crm_contacts?.name ? `${t.crm_contacts.name} · ` : ""}
                        {t.due_at ? `Due ${fmt(t.due_at)}` : "No due date"}
                      </p>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <Section title="Upcoming appointments" icon={CalendarClock} count={data.counts.upcomingAppointments}>
            {data.appointments.length === 0 ? (
              <p className="text-zinc-500 text-sm px-2 py-4">No appointments in the next 7 days.</p>
            ) : (
              <ul className="divide-y divide-white/5">
                {data.appointments.map((a: any) => (
                  <li key={a.id}>
                    <Link
                      to="/admin/customers/$projectId"
                      params={{ projectId: a.project_id }}
                      className="block px-3 py-3 rounded-lg hover:bg-white/[0.04]"
                    >
                      <p className="text-white text-sm font-medium truncate">{a.title || a.kind || "Appointment"}</p>
                      <p className="text-zinc-400 text-xs truncate">
                        {fmt(a.starts_at)}
                        {a.project?.customer_name ? ` · ${a.project.customer_name}` : ""}
                        {a.location ? ` · ${a.location}` : ""}
                      </p>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <Section title="New leads (48h)" icon={Sparkles} count={data.counts.newLeads}>
            {data.leads.length === 0 ? (
              <p className="text-zinc-500 text-sm px-2 py-4">No new leads.</p>
            ) : (
              <ul className="divide-y divide-white/5">
                {data.leads.map((l: any) => (
                  <li key={l.id}>
                    <Link
                      to="/admin/leads"
                      search={{ leadId: l.id } as any}
                      className="block px-3 py-3 rounded-lg hover:bg-white/[0.04]"
                    >
                      <p className="text-white text-sm font-medium truncate">{l.customer_name || "Lead"}</p>
                      <p className="text-zinc-400 text-xs truncate">
                        {l.service_type ? `${l.service_type} · ` : ""}{fmt(l.created_at)}
                      </p>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </div>
      )}
    </div>
  );
}